// Central business content & SEO data for Serrurier Lagny sur Marne
import {
  KeyRound,
  Siren,
  Lock,
  ShieldCheck,
  Fingerprint,
  Vault,
} from "lucide-react";

export const HERO_IMAGE =
  "https://customer-assets-agu9un31.emergentagent.net/job_local-serrurerie/artifacts/z8kbt3l0_B909CF1F-7539-48B4-980F-17F62DED2A58.png";

export const COMPANY = {
  name: "Serrurier Lagny sur Marne",
  shortName: "Serrurier Lagny",
  address: "26 Rue Freycinet, 77400 Lagny-sur-Marne",
  phoneDisplay: "01 78 76 90 00",
  phoneTel: "+33178769000",
  email: "contact@serrurier-lagny.fr",
  rating: 4.9,
  reviewsCount: 127,
  responseTime: "30 min",
  since: 2009,
};

export const BRANDS = [
  "Fichet",
  "Vachette",
  "Bricard",
  "Picard",
  "Mul-T-Lock",
  "Héraclès",
  "Vak",
  "Pollux",
  "Metalux",
  "DOM",
  "ISEO",
  "Reelax",
  "Mottura",
  "City",
];

export const CITY_REALISATIONS = new Set([
  "chessy",
  "montevrain",
  "saint-thibault-des-vignes",
  "pomponne",
  "thorigny-sur-marne",
]);

// Realisation items — description is a function of the city name for local SEO.
export const REALISATIONS = [
  {
    slug: "remplacement-serrure",
    title: "Remplacement de serrure",
    desc: (v) => `Serrure vétuste remplacée à ${v} par un modèle multipoints certifié A2P.`,
  },
  {
    slug: "securisation-portail",
    title: "Sécurisation de portail",
    desc: (v) => `Portail extérieur sécurisé à ${v} avec une serrure robuste et durable.`,
  },
  {
    slug: "porte-interieure",
    title: "Réparation de porte intérieure",
    desc: (v) => `Serrure de porte intérieure réparée et remise à neuf à ${v}.`,
  },
  {
    slug: "reparation-effraction",
    title: "Réparation après effraction",
    desc: (v) => `Porte forcée remise en sécurité à ${v} : cylindre et gâche renforcés.`,
  },
  {
    slug: "coffre-fort",
    title: "Ouverture et réparation de coffre-fort",
    desc: (v) => `Coffre-fort ouvert et réparé à ${v}, nouvelle serrure à code posée.`,
  },
  {
    slug: "remplacement-poignee",
    title: "Remplacement de poignée",
    desc: (v) => `Poignée de porte cassée remplacée à ${v} en quelques minutes.`,
  },
];

export const SERVICES = [
  {
    num: "01",
    slug: "ouverture-de-porte",
    icon: KeyRound,
    title: "Ouverture de porte",
    tagline: "Claqué ou verrouillé",
    desc: "Porte claquée, clé perdue ou serrure bloquée ? Nous ouvrons sans dégât dans la majorité des cas, avec un savoir-faire non destructif.",
    image:
      "https://images.unsplash.com/photo-1732532399621-afd080eb0b52?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    num: "02",
    slug: "depannage-urgence",
    icon: Siren,
    title: "Dépannage 24h/24",
    tagline: "7j/7 — jours fériés inclus",
    desc: "Une urgence n'attend pas. Un serrurier intervient jour et nuit sur Lagny-sur-Marne et ses environs, avec un temps de réponse moyen de 30 minutes.",
    image:
      "https://images.unsplash.com/photo-1564182842519-8a3b2af3e228?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    num: "03",
    slug: "changement-de-serrure",
    icon: Lock,
    title: "Changement de serrure",
    tagline: "Toutes marques",
    desc: "Remplacement et réparation de serrures multipoints toutes marques (Vachette, Fichet, Bricard, Picard). Devis clair avant toute intervention.",
    image:
      "https://images.unsplash.com/photo-1732532399621-afd080eb0b52?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    num: "04",
    slug: "blindage-de-porte",
    icon: ShieldCheck,
    title: "Blindage de porte",
    tagline: "Anti-effraction",
    desc: "Renforcez votre porte avec un blindage certifié pour dissuader et résister aux tentatives d'effraction. Pose soignée par nos artisans.",
    image:
      "https://images.unsplash.com/photo-1564182842519-8a3b2af3e228?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    num: "05",
    slug: "serrures-haute-securite",
    icon: Fingerprint,
    title: "Serrures haute sécurité",
    tagline: "A2P • connectées",
    desc: "Installation de serrures certifiées A2P et de solutions connectées pour une protection maximale de votre domicile ou local professionnel.",
    image:
      "https://images.unsplash.com/photo-1732532399621-afd080eb0b52?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    num: "06",
    slug: "coffres-forts",
    icon: Vault,
    title: "Coffres-forts",
    tagline: "Ouverture & pose",
    desc: "Ouverture, installation et dépannage de coffres-forts. Protégez vos biens de valeur avec des équipements adaptés à vos besoins.",
    image:
      "https://images.unsplash.com/photo-1564182842519-8a3b2af3e228?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

export const CITIES = [
  {
    slug: "lagny-sur-marne",
    name: "Lagny-sur-Marne",
    cp: "77400",
    primary: true,
    intro:
      "Notre base historique. Serrurier de proximité à Lagny-sur-Marne, nous connaissons chaque quartier — du centre-ville aux bords de Marne — pour intervenir au plus vite.",
    landmarks: ["Centre-ville", "Gare de Lagny–Thorigny", "Quartier Saint-Jean", "Bords de Marne"],
  },
  {
    slug: "thorigny-sur-marne",
    name: "Thorigny-sur-Marne",
    cp: "77400",
    intro:
      "Intervention rapide à Thorigny-sur-Marne pour tout dépannage, ouverture de porte ou changement de serrure. Un serrurier proche de chez vous.",
    landmarks: ["Gare de Lagny–Thorigny", "Le Moulin à Vent", "Centre"],
  },
  {
    slug: "pomponne",
    name: "Pomponne",
    cp: "77400",
    intro:
      "Serrurier à Pomponne : dépannage d'urgence, sécurisation et pose de serrures haute sécurité pour maisons et appartements.",
    landmarks: ["Pomponne village", "Les Berges", "Quartier de la gare"],
  },
  {
    slug: "saint-thibault-des-vignes",
    name: "Saint-Thibault-des-Vignes",
    cp: "77400",
    intro:
      "À Saint-Thibault-des-Vignes, nous assurons ouvertures de portes, blindages et installations de serrures certifiées, particuliers comme professionnels.",
    landmarks: ["Zone commerciale", "Centre bourg", "Parc des Vignes"],
  },
  {
    slug: "montevrain",
    name: "Montévrain",
    cp: "77144",
    intro:
      "Serrurier à Montévrain : interventions rapides dans les quartiers récents et pavillonnaires, dépannage 24h/24 et devis gratuit.",
    landmarks: ["Val d'Europe", "Le Clos du Chêne", "Centre-ville"],
  },
  {
    slug: "chessy",
    name: "Chessy",
    cp: "77700",
    intro:
      "Proches de Chessy et du Val d'Europe, nous intervenons pour tout problème de serrurerie, résidentiel ou commercial, à toute heure.",
    landmarks: ["Val d'Europe", "Serris limitrophe", "Gare Marne-la-Vallée–Chessy"],
  },
];


export const FAQ = [
  {
    q: "Combien de temps pour intervenir à Lagny-sur-Marne ?",
    a: "Notre délai d'intervention moyen est de 30 minutes sur Lagny-sur-Marne et les communes voisines. En cas d'urgence, nous priorisons systématiquement les personnes bloquées à l'extérieur.",
  },
  {
    q: "Intervenez-vous la nuit et les jours fériés ?",
    a: "Oui, notre service de dépannage fonctionne 24h/24 et 7j/7, y compris les week-ends et jours fériés, sans majoration abusive.",
  },
  {
    q: "Vais-je avoir un devis avant l'intervention ?",
    a: "Toujours. Nous vous communiquons un tarif clair et gratuit avant toute intervention. Aucun travail n'est engagé sans votre accord.",
  },
  {
    q: "Ouvrez-vous les portes sans les casser ?",
    a: "Dans la grande majorité des cas, oui. Nos techniciens privilégient des méthodes non destructives pour préserver votre porte et votre serrure.",
  },
  {
    q: "Quelles marques de serrures posez-vous ?",
    a: "Nous travaillons avec toutes les grandes marques : Fichet, Vachette, Bricard, Picard, Mul-T-Lock, Héraclès, Vak, Pollux, Mottura, ISEO et DOM, ainsi que des serrures certifiées A2P 1, 2 et 3 étoiles.",
  },
  {
    q: "Quel est le prix d'une ouverture de porte à Lagny-sur-Marne ?",
    a: "Le tarif dépend du type de porte et de serrure. Pour une porte simplement claquée, l'intervention est peu coûteuse. Nous vous annonçons systématiquement le prix par téléphone puis confirmons le devis gratuit sur place avant d'agir.",
  },
  {
    q: "Puis-je payer par carte bancaire ?",
    a: "Oui. Nous acceptons la carte bancaire, les espèces et les chèques. Une facture détaillée vous est toujours remise, utile notamment pour votre assurance.",
  },
  {
    q: "Que faire en cas de cambriolage ou de tentative d'effraction ?",
    a: "Contactez-nous immédiatement : nous sécurisons votre logement en urgence (remplacement de la serrure forcée, pose d'un cylindre haute sécurité, renfort des points faibles) et vous conseillons sur un blindage adapté.",
  },
  {
    q: "J'ai perdu mes clés, faut-il changer toute la serrure ?",
    a: "Pas nécessairement. Dans bien des cas, seul le cylindre (barillet) doit être remplacé, ce qui est plus économique. Après un emménagement, nous recommandons toutefois de changer le barillet par sécurité.",
  },
  {
    q: "Installez-vous des serrures certifiées A2P ?",
    a: "Oui, nous posons des serrures et cylindres certifiés A2P (anti-crochetage, anti-perçage, anti-bumping) ainsi que des serrures connectées, pour une protection reconnue par les assurances.",
  },
  {
    q: "Une clé est cassée dans la serrure, pouvez-vous l'extraire ?",
    a: "Oui, nos serruriers disposent d'outils spécifiques pour extraire une clé cassée sans endommager le mécanisme, puis vérifient le bon fonctionnement de la serrure.",
  },
  {
    q: "Intervenez-vous pour les professionnels et les copropriétés ?",
    a: "Absolument. Nous équipons et dépannons commerces, bureaux et syndics : rideaux métalliques, portes de commerce, contrôle d'accès, interphones, digicodes, badges Vigik et organigrammes de clés.",
  },
  {
    q: "Le blindage de porte est-il efficace ?",
    a: "Oui, un blindage certifié associé à une serrure multipoints augmente fortement la résistance à l'effraction, tout en améliorant l'isolation thermique et phonique de votre porte.",
  },
  {
    q: "Dans quelles villes intervenez-vous ?",
    a: "Nous intervenons à Lagny-sur-Marne, Thorigny-sur-Marne, Pomponne, Saint-Thibault-des-Vignes, Montévrain, Chessy et dans tout le secteur de Marne-la-Vallée en Seine-et-Marne (77).",
  },
  {
    q: "Puis-je obtenir un devis gratuit avant de m'engager ?",
    a: "Oui, le devis est gratuit et sans engagement. Appelez-nous ou remplissez le formulaire : nous vous rappelons rapidement pour évaluer votre besoin.",
  },
];


// -------------------------------------------------------------------------
// Contenu SEO local détaillé (~1500 mots) généré par ville pour le
// référencement naturel Google (mots-clés locaux + informations ville).
// Rendu en HTML sémantique (h2/h3/p/ul) sur chaque page ville.
// -------------------------------------------------------------------------
export function buildCityContent(city) {
  const { name, cp } = city;
  const neighbors = CITIES.filter((c) => c.slug !== city.slug);
  const neighborNames = neighbors.map((c) => c.name);
  const quartiers = city.landmarks;

  return [
    {
      h2: `Serrurier à ${name} (${cp}) : votre artisan de proximité`,
      p: [
        `Vous cherchez un serrurier à ${name} fiable, rapide et transparent ? Notre entreprise de serrurerie intervient chaque jour à ${name} et dans tout le secteur de Marne-la-Vallée pour le dépannage d'urgence, l'ouverture de porte, le changement de serrure et la sécurisation de votre domicile. Basés à Lagny-sur-Marne, à quelques minutes de ${name}, nos serruriers professionnels connaissent parfaitement la commune, ses rues, ses résidences et ses commerces. Cette proximité nous permet d'assurer une intervention rapide en 30 minutes en moyenne, de jour comme de nuit.`,
        `Qu'il s'agisse d'une porte claquée, d'une clé perdue, d'une serrure bloquée ou d'une tentative d'effraction, notre serrurier à ${name} vous apporte une solution immédiate et durable. Nous travaillons aussi bien pour les particuliers (maisons, appartements, pavillons) que pour les professionnels (commerces, bureaux, copropriétés, locaux) situés à ${name} et alentours. Chaque intervention est réalisée par un artisan qualifié, équipé d'un véhicule atelier et des outils nécessaires pour résoudre votre problème dès le premier passage.`,
        `Notre priorité à ${name} : la satisfaction et la sécurité de nos clients. C'est pourquoi nous privilégions toujours des méthodes d'ouverture non destructives, nous annonçons un tarif clair avant toute intervention et nous garantissons nos poses de serrures. Avec une note de 4,9/5 sur Google et de nombreux avis clients à ${name} et dans les communes voisines, notre sérieux et notre réactivité font la différence.`,
      ],
    },
    {
      h2: `Dépannage serrurier d'urgence 24h/24 et 7j/7 à ${name}`,
      p: [
        `Une urgence de serrurerie n'attend jamais le bon moment. Porte qui claque en pleine nuit, verrou cassé un dimanche, clé cassée dans la serrure un jour férié : notre service de dépannage serrurier à ${name} est disponible 24 heures sur 24 et 7 jours sur 7, week-ends et jours fériés compris. Un simple appel suffit pour qu'un serrurier prenne la route vers ${name} et vous dépanne dans les meilleurs délais.`,
        `Nous intervenons en urgence à ${name} pour toutes les situations : ouverture de porte claquée ou verrouillée, déblocage de serrure grippée, extraction de clé cassée, remplacement de cylindre après une perte de clés, ou encore mise en sécurité de votre logement après un cambriolage. Nos serruriers d'astreinte à ${name} arrivent équipés pour agir immédiatement, sans vous laisser bloqué à l'extérieur ni exposer votre domicile.`,
      ],
    },
    {
      h2: `Ouverture de porte à ${name} sans dégât`,
      p: [
        `L'ouverture de porte à ${name} est l'une de nos interventions les plus demandées. Porte claquée avec les clés à l'intérieur, serrure qui ne répond plus, enfant ou animal enfermé : nos serruriers maîtrisent des techniques d'ouverture fine qui permettent, dans la grande majorité des cas, d'ouvrir votre porte à ${name} sans aucun dégât et sans changer la serrure. Vous récupérez l'accès à votre logement rapidement, sans surcoût inutile.`,
        `Lorsque l'ouverture non destructive n'est pas possible (serrure de haute sécurité, porte blindée, tentative d'effraction), notre artisan serrurier à ${name} vous propose la solution la plus économique et vous remet immédiatement en état une serrure fonctionnelle. Nous transportons un large stock de cylindres et de serrures pour éviter tout deuxième déplacement.`,
      ],
    },
    {
      h2: `Changement et réparation de serrure à ${name}`,
      p: [
        `Le changement de serrure à ${name} s'impose après une perte de clés, un déménagement, une séparation, un cambriolage ou simplement l'usure du mécanisme. Notre serrurier remplace et répare tous types de serrures à ${name} : serrures en applique, à encastrer, serrures multipoints (3, 5 ou 7 points), verrous, cylindres européens et systèmes à carte de propriété. Nous installons également des cylindres débrayables qui permettent d'ouvrir même si une clé est restée à l'intérieur.`,
        `Nous travaillons toutes les grandes marques de serrurerie reconnues pour leur robustesse : Fichet, Vachette, Bricard, Picard, Mul-T-Lock, Héraclès, Vak, Pollux, Mottura ou encore ISEO. Nos serruriers vous conseillent à ${name} sur le niveau de sécurité adapté à votre porte et à votre budget, avec un devis gratuit et détaillé avant toute intervention.`,
      ],
    },
    {
      h2: `Blindage de porte et sécurisation à ${name}`,
      p: [
        `Pour protéger durablement votre logement à ${name}, rien ne vaut une porte correctement blindée. Le blindage de porte à ${name} consiste à renforcer votre porte existante avec une tôle d'acier et un cadre anti-pince, puis à y associer une serrure multipoints certifiée. Résultat : une résistance nettement supérieure aux tentatives d'effraction, un meilleur confort thermique et acoustique, et une réelle tranquillité d'esprit.`,
        `Après un cambriolage ou une tentative d'intrusion à ${name}, nous assurons également la mise en sécurité en urgence : réparation ou remplacement de la serrure forcée, pose d'un cylindre haute sécurité, renforcement des points faibles. Nos serruriers à ${name} réalisent un diagnostic gratuit de votre porte et vous proposent des solutions adaptées, du simple renfort au blindage complet certifié A2P BP.`,
      ],
    },
    {
      h2: `Serrures haute sécurité, coffres-forts et contrôle d'accès à ${name}`,
      p: [
        `Vous souhaitez renforcer la protection de votre habitation ou de votre local professionnel à ${name} ? Nous installons des serrures haute sécurité certifiées A2P (1, 2 ou 3 étoiles), des cylindres anti-crochetage et anti-perçage, des serrures connectées et des systèmes de contrôle d'accès. Nous assurons aussi l'ouverture, la pose et le dépannage de coffres-forts à ${name} pour protéger vos biens de valeur et vos documents importants.`,
      ],
    },
    {
      h2: `Nos interventions dans les quartiers de ${name}`,
      p: [
        `Notre serrurier intervient dans l'ensemble des quartiers de ${name} et à proximité immédiate. Que vous habitiez en centre-ville, en résidence ou en secteur pavillonnaire, nous connaissons les accès et pouvons rejoindre votre adresse très rapidement. Voici quelques secteurs de ${name} où nous intervenons régulièrement :`,
      ],
      list: quartiers,
    },
    {
      h2: `Serrurier autour de ${name} : nos zones d'intervention`,
      p: [
        `Au-delà de ${name}, notre serrurier couvre tout le bassin de Lagny-sur-Marne et de Marne-la-Vallée. Nous intervenons notamment à ${neighborNames.join(", ")}. Cette large couverture nous permet d'être présents sur place très vite, quelle que soit la commune, pour un dépannage d'urgence ou une intervention programmée. Retrouvez nos pages dédiées à chacune de ces villes pour en savoir plus sur nos services de serrurerie de proximité.`,
      ],
    },
    {
      h2: `Tarifs transparents et devis gratuit à ${name}`,
      p: [
        `Chez nous, pas de mauvaise surprise. Avant toute intervention à ${name}, notre serrurier vous communique un tarif clair et un devis gratuit. Vous savez exactement ce que vous payez, et aucune prestation n'est engagée sans votre accord. Nos prix de serrurerie à ${name} sont justes et adaptés à la réalité de chaque situation : ouverture simple, changement de cylindre, serrure multipoints ou blindage complet.`,
        `Nous vous remettons systématiquement une facture détaillée, indispensable notamment pour vos démarches d'assurance après un sinistre ou un cambriolage à ${name}. Cette transparence est l'une des raisons pour lesquelles nos clients à ${name} nous recommandent et nous confient à nouveau leurs travaux de serrurerie.`,
      ],
    },
    {
      h2: `Serrurier pour professionnels, commerces et syndics à ${name}`,
      p: [
        `Notre serrurier à ${name} n'intervient pas uniquement chez les particuliers. Nous accompagnons également les commerçants, les entreprises, les bureaux, les cabinets et les syndics de copropriété de ${name} pour l'entretien et la sécurisation de leurs accès. Rideaux métalliques, portes de commerce, serrures de local professionnel, systèmes de fermeture d'immeuble, portes coupe-feu, boîtes aux lettres collectives : nous assurons la maintenance, le dépannage et la mise aux normes de vos équipements de serrurerie à ${name}.`,
        `Pour les copropriétés de ${name}, nous proposons des contrats d'intervention réactifs et des solutions de contrôle d'accès (interphones, digicodes, badges Vigik) permettant de sécuriser les parties communes tout en facilitant l'accès des résidents. Nos serruriers vous conseillent sur les organigrammes de clés, très pratiques pour gérer plusieurs portes avec un nombre limité de clés.`,
      ],
    },
    {
      h2: `Conseils pour éviter les urgences de serrurerie à ${name}`,
      p: [
        `Quelques réflexes simples permettent de limiter les urgences de serrurerie à ${name}. Faites entretenir régulièrement vos serrures : un cylindre grippé ou une serrure qui force sont souvent le signe d'une panne imminente. Lubrifiez vos mécanismes avec un produit adapté (jamais d'huile qui encrasse), gardez un double de vos clés en lieu sûr et ne forcez jamais une clé qui coince, au risque de la casser dans la serrure.`,
        `Si vous emménagez dans un logement à ${name}, il est vivement recommandé de faire changer le barillet : vous ne savez jamais combien de doubles circulent. Enfin, pour renforcer durablement votre sécurité à ${name}, pensez au blindage de porte et aux serrures certifiées A2P. Notre serrurier réalise un diagnostic gratuit et vous oriente vers la solution la plus pertinente pour votre habitation.`,
      ],
    },
    {
      h2: `Pourquoi choisir notre serrurier à ${name} ?`,
      p: [
        `Choisir notre serrurier à ${name}, c'est faire appel à un artisan local, réactif et honnête. Intervention rapide en 30 minutes en moyenne, disponibilité 24h/24 et 7j/7, méthodes d'ouverture sans dégât, tarifs transparents, matériel de grandes marques et travail garanti : nous mettons tout en œuvre pour vous dépanner efficacement et sécuriser votre logement à ${name}.`,
        `Pour toute urgence ou demande de devis à ${name}, contactez dès maintenant notre serrurier au ${COMPANY.phoneDisplay}. Un professionnel vous répond immédiatement et se déplace à ${name} dans les plus brefs délais. Votre sécurité est notre métier.`,
      ],
    },
  ];
}

// -------------------------------------------------------------------------
// Pages services détaillées (SEO "service + ville")
// -------------------------------------------------------------------------
export const SERVICE_DETAILS = {
  "ouverture-de-porte": {
    intro:
      "Porte claquée, clés oubliées ou serrure bloquée : nous ouvrons votre porte sans dégât dans la grande majorité des cas, à Lagny-sur-Marne et dans toutes les communes voisines.",
    sections: [
      {
        h2: "Une ouverture sans dégât dans la majorité des cas",
        p: [
          "Une porte claquée n'exige presque jamais de perçage : grâce aux techniques d'ouverture fine (passe, radio, crochetage), nous ouvrons votre porte en quelques minutes sans abîmer la serrure ni la porte. C'est la solution la plus rapide et la plus économique.",
          "Lorsque la porte est verrouillée à clé ou que la serrure est défaillante, nous évaluons la situation avant d'agir et vous proposons toujours l'option la moins destructive possible. Le prix vous est annoncé avant toute intervention.",
        ],
      },
      {
        h2: "Dans quelles situations nous appeler ?",
        p: [
          "Porte claquée avec les clés à l'intérieur, clé perdue ou volée, serrure qui tourne dans le vide, cylindre bloqué, clé cassée dans le barillet : nous intervenons 24h/24 et 7j/7 sur ces situations, avec un délai moyen de 30 minutes sur notre secteur.",
        ],
        list: [
          "Porte claquée ou fermée à clé",
          "Clé perdue, volée ou cassée",
          "Serrure ou cylindre bloqué",
          "Serrure endommagée après tentative d'effraction",
        ],
      },
      {
        h2: "Après l'ouverture : repartir en sécurité",
        p: [
          "Si vos clés ont été perdues ou volées, ouvrir la porte ne suffit pas : nous vous recommandons le remplacement du cylindre pour qu'une ancienne clé ne puisse plus ouvrir. Nous posons des cylindres de toutes marques (Fichet, Vachette, Bricard, Picard, Mul-T-Lock) et pouvons vous conseiller un modèle certifié A2P si vous souhaitez monter en gamme.",
        ],
      },
    ],
    faq: [
      {
        q: "Pouvez-vous ouvrir ma porte sans l'abîmer ?",
        a: "Oui, dans la grande majorité des cas. Une porte simplement claquée s'ouvre sans dégât en quelques minutes. Si la serrure doit être percée (porte verrouillée, serrure haute sécurité), nous vous prévenons et vous donnons le prix avant de commencer.",
      },
      {
        q: "Combien de temps pour une ouverture de porte ?",
        a: "Sur Lagny-sur-Marne et les communes voisines, notre délai moyen d'intervention est de 30 minutes, jour et nuit, week-ends et jours fériés compris.",
      },
      {
        q: "Que faire si mes clés ont été volées ?",
        a: "Appelez-nous pour l'ouverture, puis faites remplacer le cylindre dans la foulée : c'est la seule garantie que la clé volée ne pourra plus ouvrir votre porte. Pensez également à déclarer le vol si un cambriolage est suspecté.",
      },
    ],
  },
  "depannage-urgence": {
    intro:
      "Une urgence serrurerie n'attend pas : un artisan intervient jour et nuit, 7j/7 et jours fériés, à Lagny-sur-Marne et dans un rayon d'environ 15 kilomètres.",
    sections: [
      {
        h2: "Disponible 24h/24, même la nuit et les jours fériés",
        p: [
          "Porte bloquée à 2h du matin, serrure forcée un dimanche, coffre-fort qui ne s'ouvre plus un jour férié : notre ligne d'urgence répond à toute heure. Vous parlez directement à un serrurier, pas à une plateforme : le diagnostic et le prix sont donnés immédiatement par téléphone.",
        ],
      },
      {
        h2: "Les urgences que nous traitons",
        p: [
          "Nous intervenons sur toutes les urgences de serrurerie des particuliers et des professionnels : ouverture de porte, sécurisation après effraction, serrure ou cylindre hors service, vitrine de commerce bloquée, rideau métallique coincé, coffre-fort inaccessible.",
        ],
        list: [
          "Ouverture de porte jour et nuit",
          "Mise en sécurité après cambriolage",
          "Serrure ou cylindre en panne",
          "Accès coffre-fort et rideau métallique",
        ],
      },
      {
        h2: "Un tarif annoncé avant le déplacement",
        p: [
          "Pas de mauvaise surprise : le prix de l'intervention vous est communiqué et accepté avant que le serrurier ne se déplace. Sur place, si la situation s'avère différente, le devis est ajusté et validé par vous avant tout travaux. Une facture détaillée, compatible avec les exigences des assurances, vous est systématiquement remise.",
        ],
      },
    ],
    faq: [
      {
        q: "Intervenez-vous vraiment la nuit ?",
        a: "Oui, 24h/24 et 7j/7, week-ends et jours fériés inclus. Le numéro d'urgence est le même de jour comme de nuit : 01 78 76 90 00.",
      },
      {
        q: "En combien de temps arrivez-vous ?",
        a: "Notre temps de réponse moyen est de 30 minutes sur Lagny-sur-Marne, Thorigny-sur-Marne, Pomponne, Saint-Thibault-des-Vignes, Montévrain et Chessy.",
      },
      {
        q: "Comment éviter les arnaques de serrurerie d'urgence ?",
        a: "Exigez toujours un prix ferme avant le déplacement, méfiez-vous des tarifs « à partir de » trop bas, et privilégiez un artisan local identifiable. Nous annonçons systématiquement le prix par téléphone et remettons une facture détaillée.",
      },
    ],
  },
  "changement-de-serrure": {
    intro:
      "Remplacement, réparation et mise à niveau de serrures toutes marques à Lagny-sur-Marne et environs, avec un devis clair avant toute intervention.",
    sections: [
      {
        h2: "Quand faut-il changer sa serrure ?",
        p: [
          "Perte ou vol de clés, emménagement dans un nouveau logement, serrure qui force ou qui grippe, séparation, départ d'un locataire, ou simple volonté de renforcer la sécurité : les raisons de changer une serrure sont nombreuses. Dans certains cas, le remplacement du seul cylindre suffit et coûte nettement moins cher que le bloc complet.",
        ],
      },
      {
        h2: "Cylindre ou serrure complète : on vous guide",
        p: [
          "Sur une serrure multipoints en bon état, changer uniquement le cylindre est souvent la bonne option. Sur une serrure ancienne, défaillante ou devenue trop peu sûre, le remplacement complet par une serrure multipoints certifiée A2P est préférable. Nous inspectons gratuitement votre installation et vous remettons un devis détaillé.",
        ],
        list: [
          "Cylindres et barillets toutes marques",
          "Serrures multipoints 3, 5 et 7 points",
          "Serrures en applique ou encastrées",
          "Marques : Fichet, Vachette, Bricard, Picard, Mul-T-Lock, Héraclès, ISEO, DOM",
        ],
      },
      {
        h2: "Une pose propre et garantie",
        p: [
          "Nos serruriers travaillent proprement : protection du chantier, réglage des gâches, vérification du fonctionnement avec vous avant de partir. Toutes nos installations sont garanties, et nous restons disponibles en cas de réglage nécessaire après la pose.",
        ],
      },
    ],
    faq: [
      {
        q: "Faut-il changer toute la serrure après une perte de clés ?",
        a: "Non, le plus souvent le remplacement du cylindre suffit : c'est plus rapide et moins coûteux. Nous vous le confirmons après inspection.",
      },
      {
        q: "Combien de temps prend un changement de serrure ?",
        a: "Un remplacement de cylindre prend généralement moins de 30 minutes. Une serrure multipoints complète demande entre 1 et 2 heures selon la porte.",
      },
      {
        q: "Travaillez-vous avec ma marque de serrure ?",
        a: "Oui, nous installons et réparons toutes les grandes marques : Fichet, Vachette, Bricard, Picard, Mul-T-Lock, Héraclès, Vak, Pollux, Mottura, ISEO et DOM.",
      },
    ],
  },
  "blindage-de-porte": {
    intro:
      "Renforcez durablement votre porte d'entrée avec un blindage certifié : la solution la plus dissuasive contre les effractions, posée à Lagny-sur-Marne et dans tout le secteur.",
    sections: [
      {
        h2: "Le blindage : la protection la plus dissuasive",
        p: [
          "Un blindage consiste à renforcer votre porte existante d'une tôle d'acier, d'un cadre anti-pince et de cornières renforcées, sans changer l'esthétique de votre entrée. Combiné à une serrure multipoints certifiée A2P, il transforme votre porte en véritable obstacle : la grande majorité des cambrioleurs renonce face à une porte blindée.",
        ],
      },
      {
        h2: "Blindage de la porte existante ou porte blindée neuve ?",
        p: [
          "Si votre porte actuelle est saine et en bois massif, le blindage sur place est la solution la plus économique et la plus discrète. Si la porte est ancienne, creuse ou endommagée, le remplacement par un bloc-porte blindé certifié est préférable. Nous vous conseillons objectivement après un diagnostic gratuit à domicile.",
        ],
        list: [
          "Tôle d'acier et cadre anti-pince",
          "Cornières de renfort",
          "Serrure multipoints A2P associée",
          "Amélioration de l'isolation thermique et phonique",
        ],
      },
      {
        h2: "Un investissement valorisé par votre assurance",
        p: [
          "Beaucoup de contrats d'assurance habitation valorisent — parfois exigent — une porte d'entrée renforcée avec serrure certifiée A2P. Nous vous remettons une facture détaillée mentionnant les certifications des équipements posés, utile en cas de sinistre comme de négociation de contrat.",
        ],
      },
    ],
    faq: [
      {
        q: "Le blindage change-t-il l'aspect de ma porte ?",
        a: "Non, le blindage est posé à l'intérieur du battant : votre porte garde exactement le même aspect extérieur, un point important en copropriété.",
      },
      {
        q: "Faut-il être propriétaire pour blinder sa porte ?",
        a: "Locataire ou propriétaire, c'est possible. En location, un accord écrit du bailleur est recommandé. En copropriété, le blindage à aspect conservé ne nécessite généralement pas d'autorisation, mais vérifiez votre règlement.",
      },
      {
        q: "Blindage ou porte blindée : que choisir ?",
        a: "Sur une porte en bois massif saine, le blindage sur place est plus économique et tout aussi efficace. Sur une porte fragile ou très ancienne, un bloc-porte blindé neuf est préférable. Notre diagnostic gratuit tranche objectivement.",
      },
    ],
  },
  "serrures-haute-securite": {
    intro:
      "Serrures et cylindres certifiés A2P, clés protégées, serrures connectées : montez en gamme sur la sécurité avec du matériel reconnu par les assureurs.",
    sections: [
      {
        h2: "La certification A2P, la référence des assureurs",
        p: [
          "La norme A2P classe les serrures selon leur temps de résistance à l'effraction : 1 étoile (5 minutes), 2 étoiles (10 minutes), 3 étoiles (15 minutes). Plus le niveau est élevé, plus votre porte résiste à un cambrioleur outillé. La plupart des assurances exigent au minimum une serrure A2P 1 étoile pour couvrir un vol par effraction.",
        ],
      },
      {
        h2: "Cylindres haute sécurité et clés protégées",
        p: [
          "Anti-perçage, anti-crochetage, anti-arrachement : les cylindres haute sécurité intègrent des protections mécaniques avancées. Les modèles à clé protégée (Mul-T-Lock, DOM, ISEO…) exigent la présentation d'une carte de propriété pour toute reproduction de clé : personne ne peut faire copier vos clés sans votre accord.",
        ],
        list: [
          "Serrures certifiées A2P 1, 2 et 3 étoiles",
          "Cylindres anti-perçage et anti-arrachement",
          "Clés protégées par carte de propriété",
          "Organigrammes de clés pour professionnels",
        ],
      },
      {
        h2: "Et les serrures connectées ?",
        p: [
          "Ouverture par code, badge ou smartphone, journal des passages, clés temporaires pour un locataire ou un intervenant : la serrure connectée apporte un vrai confort au quotidien. Nous vous présentons les avantages et les précautions de chaque modèle, et nous privilégions les marques reconnues pour leur fiabilité.",
        ],
      },
    ],
    faq: [
      {
        q: "Quelle différence entre A2P 1, 2 et 3 étoiles ?",
        a: "Le nombre d'étoiles correspond au temps de résistance certifié à l'effraction : 5, 10 ou 15 minutes. Pour un appartement, 1 étoile suffit souvent ; pour un pavillon isolé, 2 ou 3 étoiles sont recommandées.",
      },
      {
        q: "Une serrure connectée est-elle vraiment sûre ?",
        a: "Oui, à condition de choisir un modèle reconnu, correctement installé et tenu à jour. Nous vous conseillons des modèles éprouvés et vous expliquons les bonnes pratiques (codes, accès, mises à jour).",
      },
      {
        q: "Mon assurance rembourse-t-elle le changement de serrure ?",
        a: "Après un cambriolage ou une tentative, la plupart des contrats prennent en charge la remise en sécurité. Notre facture détaillée avec les certifications A2P facilite le dossier.",
      },
    ],
  },
  "coffres-forts": {
    intro:
      "Ouverture de coffre-fort bloqué, pose et scellement de coffres : une expertise discrète et soigneuse pour les particuliers et les professionnels du secteur.",
    sections: [
      {
        h2: "Ouverture de coffre-fort bloqué",
        p: [
          "Combinaison oubliée, clé perdue, mécanisme grippé ou serrure électronique en panne : nous ouvrons votre coffre-fort en privilégiant les méthodes non destructives, afin de préserver à la fois le coffre et son contenu. Chaque intervention est réalisée avec discrétion, sur justificatif de propriété.",
        ],
      },
      {
        h2: "Pose et scellement de coffres",
        p: [
          "Un coffre-fort n'est efficace que s'il est correctement scellé : un modèle non fixé peut être emporté entier. Nous posons et scellons les coffres à poser, à encastrer et les modèles ignifuges, en respectant les préconisations des fabricants et les exigences des assurances.",
        ],
        list: [
          "Ouverture fine de coffres bloqués",
          "Pose et scellement dans la dalle ou le mur",
          "Coffres ignifuges et certifiés",
          "Maintenance et changement de serrure de coffre",
        ],
      },
      {
        h2: "Choisir le bon niveau de protection",
        p: [
          "Valeur assurable, résistance au feu, volume, fréquence d'accès : le choix d'un coffre dépend de votre usage. Nous vous aidons à définir le niveau de certification adapté à ce que vous souhaitez protéger, sans surinvestissement inutile.",
        ],
      },
    ],
    faq: [
      {
        q: "Pouvez-vous ouvrir un coffre sans l'endommager ?",
        a: "Dans de nombreux cas, oui : nous privilégions toujours les méthodes non destructives. Si une ouverture destructive est inévitable, nous vous en informons avant et le coffre peut souvent être remis en service après remplacement de la serrure.",
      },
      {
        q: "Faut-il sceller un coffre-fort ?",
        a: "C'est fortement recommandé : un coffre non scellé peut être emporté. Le scellement dans une dalle béton ou un mur porteur est souvent exigé par les assurances au-delà d'une certaine valeur.",
      },
      {
        q: "Intervenez-vous pour les professionnels ?",
        a: "Oui, nous travaillons avec commerces, pharmacies, bureaux et copropriétés du secteur : ouvertures, poses, maintenances et mises aux normes.",
      },
    ],
  },
};

// -------------------------------------------------------------------------
// FAQ localisées par ville (données structurées FAQPage sur chaque page ville)
// -------------------------------------------------------------------------
export const CITY_FAQS = {
  "lagny-sur-marne": [
    {
      q: "En combien de temps un serrurier peut-il intervenir à Lagny-sur-Marne ?",
      a: "Lagny-sur-Marne est notre base : notre délai moyen d'intervention est de 30 minutes, que vous soyez au centre-ville, vers la gare de Lagny–Thorigny, au quartier Saint-Jean ou sur les bords de Marne. Nous sommes disponibles 24h/24 et 7j/7.",
    },
    {
      q: "Intervenez-vous dans tous les quartiers de Lagny-sur-Marne (77400) ?",
      a: "Oui, nous couvrons l'intégralité de la commune : centre-ville, gare de Lagny–Thorigny, quartier Saint-Jean, bords de Marne et tous les lotissements résidentiels, pour les maisons comme pour les appartements.",
    },
    {
      q: "Faites-vous les devis gratuitement à Lagny-sur-Marne ?",
      a: "Oui, le devis est gratuit. Pour les urgences, le prix est annoncé par téléphone avant le déplacement. Pour les travaux (blindage, serrure multipoints, coffre-fort), nous nous déplaçons gratuitement pour établir un devis détaillé.",
    },
    {
      q: "Pouvez-vous sécuriser une porte de commerce à Lagny-sur-Marne ?",
      a: "Oui, nous travaillons avec les commerces et professionnels de Lagny-sur-Marne : rideaux métalliques, serrures de vitrine, contrôle d'accès, organigrammes de clés et mise en sécurité après effraction.",
    },
    {
      q: "Que faire en cas de cambriolage à Lagny-sur-Marne ?",
      a: "Appelez d'abord le 17 et ne touchez à rien, puis portez plainte. Contactez-nous ensuite pour la mise en sécurité immédiate : remplacement de la serrure forcée et renfort des points faibles. Nous fournissons la facture détaillée exigée par votre assurance.",
    },
  ],
  "thorigny-sur-marne": [
    {
      q: "Quel est le délai d'intervention d'un serrurier à Thorigny-sur-Marne ?",
      a: "Thorigny-sur-Marne jouxte notre base de Lagny : notre délai moyen est de 30 minutes, du centre au quartier du Moulin à Vent en passant par les abords de la gare de Lagny–Thorigny, 24h/24 et 7j/7.",
    },
    {
      q: "Couvrez-vous tout Thorigny-sur-Marne (77400) ?",
      a: "Oui, l'ensemble de la commune : le centre, le Moulin à Vent, les abords de la gare de Lagny–Thorigny et tous les secteurs résidentiels, pour les pavillons comme pour les appartements.",
    },
    {
      q: "Une porte claquée à Thorigny, combien ça coûte ?",
      a: "Le prix exact dépend de votre serrure et de l'horaire : il vous est annoncé fermement par téléphone avant tout déplacement, sans surprise. Une porte claquée s'ouvre dans la plupart des cas sans dégât.",
    },
    {
      q: "Pouvez-vous renforcer la porte d'un pavillon à Thorigny-sur-Marne ?",
      a: "Oui, nous sécurisons régulièrement les pavillons de Thorigny : serrure multipoints certifiée A2P, blindage de la porte existante ou bloc-porte blindé, cornières anti-pince. Diagnostic et devis gratuits à domicile.",
    },
    {
      q: "Intervenez-vous la nuit à Thorigny-sur-Marne ?",
      a: "Oui, notre service d'urgence fonctionne 24h/24, week-ends et jours fériés compris, avec le même numéro : 01 78 76 90 00.",
    },
  ],
  "pomponne": [
    {
      q: "Trouver un serrurier rapidement à Pomponne, c'est possible ?",
      a: "Oui : Pomponne est à quelques minutes de notre base de Lagny-sur-Marne. Notre délai moyen d'intervention est de 30 minutes, au village comme aux Berges ou vers le quartier de la gare, 24h/24.",
    },
    {
      q: "Intervenez-vous dans toute la commune de Pomponne (77400) ?",
      a: "Oui : Pomponne village, Les Berges, le quartier de la gare et tous les secteurs pavillonnaires. Nous intervenons sur les maisons, appartements, commerces et dépendances.",
    },
    {
      q: "Mes clés sont restées dans la serrure à l'intérieur, que faire ?",
      a: "Ne forcez pas : une clé restée à l'intérieur se gère proprement dans la plupart des cas. Appelez-nous, nous ouvrons sans dégât et pouvons poser un cylindre débrayable pour éviter que cela se reproduise.",
    },
    {
      q: "Faites-vous le blindage de porte à Pomponne ?",
      a: "Oui, le blindage est l'une de nos spécialités sur les pavillons de Pomponne : renfort de la porte existante ou bloc-porte blindé, associé à une serrure multipoints certifiée A2P. Devis gratuit à domicile.",
    },
    {
      q: "Le devis est-il payant à Pomponne ?",
      a: "Non, il est gratuit : par téléphone pour les urgences, et avec déplacement gratuit pour les travaux de sécurisation (blindage, multipoints, coffre-fort).",
    },
  ],
  "saint-thibault-des-vignes": [
    {
      q: "En combien de temps intervenez-vous à Saint-Thibault-des-Vignes ?",
      a: "Saint-Thibault-des-Vignes est dans notre zone de proximité immédiate : délai moyen de 30 minutes, du centre bourg à la zone commerciale et au parc des Vignes, 24h/24 et 7j/7.",
    },
    {
      q: "Couvrez-vous tout Saint-Thibault-des-Vignes (77400) ?",
      a: "Oui, toute la commune : centre bourg, zone commerciale, parc des Vignes et secteurs résidentiels, pour les particuliers comme pour les professionnels.",
    },
    {
      q: "Pouvez-vous dépanner la serrure d'une boutique ou d'un local ?",
      a: "Oui, nous intervenons régulièrement auprès des commerces et entreprises de la zone commerciale de Saint-Thibault : serrures de vitrine, rideaux métalliques, contrôle d'accès et urgences d'ouverture.",
    },
    {
      q: "Comment sécuriser un appartement à Saint-Thibault-des-Vignes ?",
      a: "Nous commençons par un diagnostic gratuit : serrure multipoints certifiée A2P, cylindre anti-perçage à clé protégée, renfort des points faibles. En copropriété, nous respectons les contraintes esthétiques de l'immeuble.",
    },
    {
      q: "Intervenez-vous le dimanche à Saint-Thibault-des-Vignes ?",
      a: "Oui, dimanches et jours fériés inclus : notre urgence serrurerie fonctionne 24h/24 et 7j/7 au 01 78 76 90 00.",
    },
  ],
  "montevrain": [
    {
      q: "Un serrurier peut-il venir vite à Montévrain ?",
      a: "Oui : Montévrain (77144) fait partie de notre secteur d'intervention quotidien. Délai moyen de 30 minutes, du centre-ville au Clos du Chêne et aux abords de Val d'Europe, 24h/24.",
    },
    {
      q: "Intervenez-vous dans les résidences récentes de Montévrain ?",
      a: "Oui, nous intervenons dans toutes les résidences, y compris récentes : portes palières, serrures multipoints d'origine, cylindres à clé protégée, digicodes et contrôle d'accès, dans le respect des règles de copropriété.",
    },
    {
      q: "Je viens d'emménager à Montévrain, faut-il changer la serrure ?",
      a: "C'est fortement recommandé : impossible de savoir qui détient encore un double des clés. Le remplacement du cylindre suffit le plus souvent — intervention rapide et devis gratuit.",
    },
    {
      q: "Faites-vous les serrures connectées à Montévrain ?",
      a: "Oui, nous posons des serrures connectées (code, badge, smartphone) adaptées aux logements neufs comme anciens, avec présentation honnête des avantages et des précautions d'usage.",
    },
    {
      q: "Travaillez-vous avec les commerces de Val d'Europe ?",
      a: "Oui, nous dépannons les professionnels du secteur Val d'Europe / Montévrain : serrures de vitrine, rideaux métalliques, coffres-forts et contrôle d'accès, avec facture détaillée.",
    },
  ],
  "chessy": [
    {
      q: "Quel est votre délai d'intervention à Chessy ?",
      a: "Chessy (77700) est couverte quotidiennement : délai moyen de 30 minutes, du village aux abords de la gare Marne-la-Vallée–Chessy et de Val d'Europe, 24h/24 et 7j/7.",
    },
    {
      q: "Couvrez-vous tout Chessy et les alentours ?",
      a: "Oui : Chessy village, les secteurs proches de la gare Marne-la-Vallée–Chessy, de Val d'Europe et les communes limitrophes comme Serris. Maisons, appartements, commerces et locations touristiques.",
    },
    {
      q: "Vous occupez-vous des locations saisonnières et Airbnb ?",
      a: "Oui, nous équipons les locations saisonnières de Chessy : serrures connectées à codes temporaires, boîtes à clés sécurisées et dépannage rapide entre deux séjours.",
    },
    {
      q: "Clé cassée dans la serrure à Chessy : comment ça se passe ?",
      a: "Nous extrayons le fragment sans dégât dans la plupart des cas, puis vérifions l'état du cylindre. Si nécessaire, nous le remplaçons sur place. Le prix est annoncé avant l'intervention.",
    },
    {
      q: "Intervenez-vous la nuit à Chessy ?",
      a: "Oui, de jour comme de nuit, week-ends et jours fériés compris : un seul numéro, 01 78 76 90 00, avec un prix annoncé avant le déplacement.",
    },
  ],
};

